// API Service - Frontend API client
// All database operations go through the backend API

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001/api';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  code?: string;
}

interface FormData {
  name: string;
  company: string;
  phone: string;
  email?: string;
  industry: string;
  industry_other?: string;
  product: string;
  message?: string;
}

interface ConsultationForm {
  id: number;
  name: string;
  company: string;
  phone: string;
  email: string | null;
  industry: string | null;
  product: string | null;
  message: string | null;
  status: 'pending' | 'processed' | 'closed';
  notes: string | null;
  ip_address: string | null;
  created_at: string;
  updated_at: string;
}

interface SiteContent {
  id: number;
  page_type: string;
  content: any;
  is_published: boolean;
  published_at: string | null;
  version: number;
}

interface PublishHistory {
  id: number;
  version: number;
  content_summary: any;
  published_by: string | null;
  status: string;
  created_at: string;
}

interface VisitorLog {
  id: number;
  ip_address: string;
  user_agent: string;
  page_url: string;
  referrer: string | null;
  session_id: string;
  created_at: string;
}

interface VisitorStats {
  today: number;
  yesterday: number;
  thisWeek: number;
  lastWeek: number;
  thisMonth: number;
  lastMonth: number;
  total: number;
  dailyTrend: Array<{ date: string; count: number }>;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || `HTTP ${response.status}`);
  }

  return data;
}

// Form API
export const formApi = {
  async submit(formData: FormData): Promise<ApiResponse<ConsultationForm>> {
    return fetchApi('/forms/submit', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  },

  async getAll(): Promise<ApiResponse<ConsultationForm[]>> {
    return fetchApi('/forms');
  },

  async updateStatus(
    id: number, 
    status: 'pending' | 'processed' | 'closed'
  ): Promise<ApiResponse<ConsultationForm>> {
    return fetchApi(`/forms/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  async updateNotes(id: number, notes: string): Promise<ApiResponse<ConsultationForm>> {
    return fetchApi(`/forms/${id}/notes`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  },
};

// Content API
export const contentApi = {
  async getPublished(pageType: string): Promise<ApiResponse<SiteContent | null>> {
    return fetchApi(`/content/published/${pageType}`);
  },

  async getAll(): Promise<ApiResponse<SiteContent[]>> {
    return fetchApi('/content');
  },

  async save(pageType: string, content: any): Promise<ApiResponse<SiteContent>> {
    return fetchApi('/content', {
      method: 'POST',
      body: JSON.stringify({ pageType, content }),
    });
  },

  async publish(pageType: string): Promise<ApiResponse<SiteContent>> {
    return fetchApi(`/content/publish/${pageType}`, {
      method: 'POST',
    });
  },

  async getConfig(key: string): Promise<ApiResponse<any>> {
    return fetchApi(`/content/config/${key}`);
  },

  async updateConfig(key: string, value: any): Promise<ApiResponse<SiteContent>> {
    return fetchApi(`/content/config/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

  async getHistory(): Promise<ApiResponse<PublishHistory[]>> {
    return fetchApi('/content/history');
  },
};

// Visitor API
export const visitorApi = {
  async track(pageUrl: string, referrer: string = ''): Promise<ApiResponse<VisitorLog>> {
    return fetchApi('/visitors/track', {
      method: 'POST',
      body: JSON.stringify({ pageUrl, referrer }),
    });
  },

  async getStats(): Promise<ApiResponse<VisitorStats>> {
    return fetchApi('/visitors/stats');
  },

  async getLogs(limit: number = 100): Promise<ApiResponse<VisitorLog[]>> {
    return fetchApi(`/visitors/logs?limit=${limit}`);
  },
};

export default { formApi, contentApi, visitorApi };
